'use client';

import { c as _c } from "react/compiler-runtime";
import ViverseCommunityDialog from '@/components/ViverseCommunityDialog';
import dynamic from 'next/dynamic';
import { usePathname, useSearchParams } from 'next/navigation';
import { use, useEffect, useRef, useState } from 'react';
import { useIsClient } from 'usehooks-ts';
import { SEARCH_PARAM_KEYS } from '@/constants/auth';
import { useUAContext } from '@/context/UAContext';
import { useVSCSdkContext } from '@/context/VSCSdkContext';
import { useCalculateHeaderHeight } from '@/hooks/useCalculateHeaderHeight';
import { useEnableFull3d } from '@/hooks/useEnableFull3d';
import { useIsPublicProfileVideoPage } from '@/hooks/useIsPublicProfileVideoPage';
import { useShareComponentBaseConfig } from '@/hooks/useShareComponentBaseConfig';
import { useShareComponentEvents } from '@/hooks/useShareComponentEvents';
import { getHeaderTab, type HeaderTab } from '@/lib/utils';
import { useFeatureFlag } from '@posthog/next';
const ShareComponentHeader = dynamic(() => import('@viveportengineering/share-component').then(mod => mod.Header));
const HEADER_DRAWER_ACTION_FLAG = 'header-drawer-action';
type DesktopDrawerInteractionMode = 'hover' | 'click';
type ShareComponentProfile = {
  displayName?: string;
  activeAvatar?: {
    id?: string;
    snapshot_url?: string;
    avatar_url?: string;
    head_icon_url?: string;
    gender?: number;
    data_type?: number;
    is_half_body?: boolean;
  };
  userID?: string;
  points?: number;
  token?: string;
  accountId?: string;
};
type HeaderClientProps = {
  shareComponentProfilePromise: Promise<ShareComponentProfile>;
  isCreateDomain: boolean;
};
export default function HeaderClient(t0) {
  const $ = _c(34);
  const {
    shareComponentProfilePromise,
    isCreateDomain
  } = t0;
  const initialShareComponentProfile = use(shareComponentProfilePromise);
  const headerRef = useRef(null);
  const [openCommunityDialog, setOpenCommunityDialog] = useState(false);
  const headerDrawerFlag = useFeatureFlag(HEADER_DRAWER_ACTION_FLAG);
  const t1 = headerDrawerFlag?.variant;
  let t2;
  if ($[0] !== t1) {
    t2 = toDesktopDrawerInteractionMode(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const desktopDrawerInteractionMode = t2;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t3;
  if ($[2] !== isCreateDomain || $[3] !== pathname) {
    t3 = isCreateDomain ? "CREATE" : getHeaderTab(pathname);
    $[2] = isCreateDomain;
    $[3] = pathname;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const tab = t3;
  let t4;
  if ($[5] !== pathname) {
    t4 = pathname.includes("examples");
    $[5] = pathname;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const isExamplePage = t4;
  let t5;
  if ($[7] !== searchParams) {
    t5 = searchParams.has(SEARCH_PARAM_KEYS.PREVIEW);
    $[7] = searchParams;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const previewMode = t5;
  const isClient = useIsClient();
  const {
    isMobileApp
  } = useUAContext();
  let t6;
  if ($[9] !== initialShareComponentProfile || $[10] !== previewMode) {
    t6 = {
      previewMode,
      profile: initialShareComponentProfile
    };
    $[9] = initialShareComponentProfile;
    $[10] = previewMode;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const {
    authConfig,
    device,
    localeConfig,
    originConfig
  } = useShareComponentBaseConfig(t6);
  const {
    setIsMountedHeader
  } = useVSCSdkContext();
  const enableFull3d = useEnableFull3d();
  const isPublicProfileVideoPage = useIsPublicProfileVideoPage();
  const shouldHideHeader = enableFull3d || isExamplePage || isMobileApp || isPublicProfileVideoPage;
  useCalculateHeaderHeight(headerRef);
  useShareComponentEvents();
  let t7;
  let t8;
  if ($[12] !== setIsMountedHeader || $[13] !== shouldHideHeader) {
    t7 = () => {
      if (shouldHideHeader) {
        setIsMountedHeader(false);
        return;
      }
      setIsMountedHeader(true);
      return () => {
        setIsMountedHeader(false);
      };
    };
    t8 = [setIsMountedHeader, shouldHideHeader];
    $[12] = setIsMountedHeader;
    $[13] = shouldHideHeader;
    $[14] = t7;
    $[15] = t8;
  } else {
    t7 = $[14];
    t8 = $[15];
  }
  useEffect(t7, t8);
  const t9 = isCreateDomain ? originConfig.create : originConfig.viverse;
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      showIframe: () => setOpenCommunityDialog(true)
    };
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== desktopDrawerInteractionMode || $[18] !== t9 || $[19] !== tab) {
    t11 = {
      currentOrigin: t9,
      desktopDrawerInteractionMode,
      tab,
      community: t10
    };
    $[17] = desktopDrawerInteractionMode;
    $[18] = t9;
    $[19] = tab;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const layoutConfig = t11;
  if (shouldHideHeader) {
    return null;
  }
  let t12;
  if ($[21] !== authConfig || $[22] !== device || $[23] !== layoutConfig || $[24] !== localeConfig || $[25] !== originConfig) {
    t12 = <header ref={headerRef} className="z-navigation fixed top-0 h-auto w-full bg-transparent transition-all"><ShareComponentHeader key={localeConfig.locale} authConfig={authConfig} localeConfig={localeConfig} layoutConfig={layoutConfig} origin={originConfig} device={device} /></header>;
    $[21] = authConfig;
    $[22] = device;
    $[23] = layoutConfig;
    $[24] = localeConfig;
    $[25] = originConfig;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== enableFull3d || $[28] !== isClient || $[29] !== openCommunityDialog) {
    t13 = !enableFull3d && isClient && <ViverseCommunityDialog open={openCommunityDialog} onOpenChange={setOpenCommunityDialog} />;
    $[27] = enableFull3d;
    $[28] = isClient;
    $[29] = openCommunityDialog;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t12 || $[32] !== t13) {
    t14 = <>{t12}{t13}</>;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  return t14;
}
function toDesktopDrawerInteractionMode(value: string | boolean | undefined): DesktopDrawerInteractionMode {
  return value === 'hover' ? 'hover' : 'click';
}